import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

const SWIPE_THRESHOLD = 60;
const SWIPE_VELOCITY_THRESHOLD = 450;

function CamisetasModal({
    camisetasPair,
    isOpen,
    onClose,
    onPrev,
    onNext,
    hasPrev,
    hasNext,
    isTransitioning = false,
}) {
    const [loadedImages, setLoadedImages] = useState({});
    const [isSwipeEnabled, setIsSwipeEnabled] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const [selectedVariantId, setSelectedVariantId] = useState(null);
    const wasOpenRef = useRef(false);

    useEffect(() => {
        if (!isOpen) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
                return;
            }

            if (event.key === "ArrowLeft" && hasPrev) {
                onPrev();
                return;
            }

            if (event.key === "ArrowRight" && hasNext) {
                onNext();
            }
        };

        window.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [isOpen, onClose, onPrev, onNext, hasPrev, hasNext]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const evaluateSwipeSupport = () => {
            const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
            const isMobileWidth = window.innerWidth < 768;
            setIsMobileLayout(isMobileWidth);
            setIsSwipeEnabled(isTouchDevice || isMobileWidth);
        };

        evaluateSwipeSupport();
        window.addEventListener("resize", evaluateSwipeSupport);

        return () => {
            window.removeEventListener("resize", evaluateSwipeSupport);
        };
    }, []);

    const variants = useMemo(
        () => [
            {
                id: "rojiverde",
                label: "Rojiverde",
                camiseta: camisetasPair?.rojiverde,
            },
            {
                id: "alternativa",
                label: "Alternativa",
                camiseta: camisetasPair?.alternativa,
            },
        ].filter((variant) => Boolean(variant.camiseta)),
        [camisetasPair]
    );
    const isSpecialEdition =
        camisetasPair?.title === "LUB 2021/22" &&
        variants.length === 1 &&
        variants[0].id === "rojiverde";
    const initialVariantId = camisetasPair?.initialVariantId;

    useEffect(() => {
        const hasJustOpened = isOpen && !wasOpenRef.current;

        if (hasJustOpened && isMobileLayout) {
            const matchingRequestedVariantId = variants.find(
                (variant) => variant.id === initialVariantId
            )?.id;
            const defaultVariantId =
                matchingRequestedVariantId ??
                variants.find((variant) => variant.id === "rojiverde")?.id ??
                variants[0]?.id ??
                null;
            setSelectedVariantId(defaultVariantId);
        }

        wasOpenRef.current = isOpen;
    }, [isOpen, isMobileLayout, variants, initialVariantId]);

    const selectedMobileVariant = useMemo(() => {
        if (!isMobileLayout) {
            return null;
        }

        return (
            variants.find((variant) => variant.id === selectedVariantId) ?? variants[0] ?? null
        );
    }, [isMobileLayout, selectedVariantId, variants]);

    const mobileNavButtonClass =
        "relative inline-flex min-h-11 flex-1 items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-[#0f1f15] px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-white transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-40";

    const visibleImageSources = useMemo(
        () => variants.map((variant) => variant.camiseta?.image).filter(Boolean),
        [variants]
    );

    useEffect(() => {
        setLoadedImages((previousLoadedImages) => {
            const nextLoadedImages = { ...previousLoadedImages };

            visibleImageSources.forEach((source) => {
                if (nextLoadedImages[source]) {
                    return;
                }

                const preloadedImage = new Image();
                preloadedImage.src = source;

                if (preloadedImage.complete) {
                    nextLoadedImages[source] = true;
                    return;
                }

                preloadedImage.onload = () => {
                    setLoadedImages((currentLoadedImages) => {
                        if (currentLoadedImages[source]) {
                            return currentLoadedImages;
                        }

                        return {
                            ...currentLoadedImages,
                            [source]: true,
                        };
                    });
                };

                preloadedImage.onerror = () => {
                    setLoadedImages((currentLoadedImages) => ({
                        ...currentLoadedImages,
                        [source]: true,
                    }));
                };
            });

            return nextLoadedImages;
        });
    }, [visibleImageSources]);

    const handleImageLoad = (source) => {
        if (!source) {
            return;
        }

        setLoadedImages((previousLoadedImages) => {
            if (previousLoadedImages[source]) {
                return previousLoadedImages;
            }

            return {
                ...previousLoadedImages,
                [source]: true,
            };
        });
    };

    const handleDragEnd = (_event, info) => {
        if (!isSwipeEnabled || isTransitioning) {
            return;
        }

        const movedRight =
            info.offset.x > SWIPE_THRESHOLD || info.velocity.x > SWIPE_VELOCITY_THRESHOLD;
        const movedLeft =
            info.offset.x < -SWIPE_THRESHOLD || info.velocity.x < -SWIPE_VELOCITY_THRESHOLD;

        if (movedRight && hasPrev) {
            onPrev();
            return;
        }

        if (movedLeft && hasNext) {
            onNext();
        }
    };

    if (!isOpen || !camisetasPair) {
        return null;
    }

    const modalContent = (
        <AnimatePresence mode="wait">
            {isOpen && camisetasPair ? (
                <motion.div
                    className="fixed inset-0 z-[120] flex min-h-[100dvh] items-center justify-center overflow-y-auto overflow-x-hidden bg-black/80 p-3 backdrop-blur-sm sm:p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="relative w-full max-w-[min(92vw,560px)] rounded-xl border border-white/20 bg-[#0b1a12] p-3 sm:p-5 shadow-2xl max-h-[calc(100dvh-1.5rem)] overflow-x-hidden overflow-y-auto"
                        initial={{ scale: 0.95, opacity: 0, y: 10 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        drag={isSwipeEnabled && !isTransitioning ? "x" : false}
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={0.22}
                        dragMomentum={true}
                        dragTransition={{ bounceStiffness: 420, bounceDamping: 28 }}
                        onDragEnd={handleDragEnd}
                        whileDrag={{ scale: 0.985, cursor: "grabbing" }}
                        style={{ touchAction: "pan-y", willChange: "transform" }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={onPrev}
                            disabled={!hasPrev}
                            className={`absolute hidden h-10 w-10 -translate-y-1/2 rounded-full border border-white/20 bg-black/50 text-white z-20 disabled:cursor-not-allowed disabled:opacity-40 hover:bg-black/70 sm:-left-10 sm:top-1/2 sm:block md:-left-14 ${isTransitioning ? "cursor-wait" : ""}`}
                            aria-label="Camiseta anterior"
                        >
                            ←
                        </button>

                        <button
                            type="button"
                            onClick={onNext}
                            disabled={!hasNext}
                            className={`absolute hidden h-10 w-10 -translate-y-1/2 rounded-full border border-white/20 bg-black/50 text-white z-20 disabled:cursor-not-allowed disabled:opacity-40 hover:bg-black/70 sm:-right-10 sm:top-1/2 sm:block md:-right-14 ${isTransitioning ? "cursor-wait" : ""}`}
                            aria-label="Camiseta siguiente"
                        >
                            →
                        </button>

                        <button
                            type="button"
                            onClick={onClose}
                            className="absolute right-3 top-3 z-20 h-9 w-9 rounded-full border border-white/20 bg-black/30 text-white hover:bg-black/50"
                            aria-label="Cerrar modal"
                        >
                            x
                        </button>

                        <div className="space-y-3">
                            <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-white border-l-4 border-red-500 pl-3">
                                {camisetasPair.title}
                            </h3>

                            {isMobileLayout ? (
                                <div className="space-y-3">
                                    {variants.length > 1 ? (
                                        <div className="inline-flex w-full gap-1 rounded-xl border border-white/20 bg-[#0f1f15] p-1 shadow-2xl">
                                            {variants.map((variant) => {
                                                const isSelected = selectedMobileVariant?.id === variant.id;

                                                return (
                                                    <button
                                                        key={variant.id}
                                                        type="button"
                                                        onClick={() => setSelectedVariantId(variant.id)}
                                                        className={`relative z-10 flex-1 rounded-lg px-3 py-2 text-[10px] font-extrabold uppercase tracking-wide transition-colors duration-200 ${isSelected ? "text-white" : "text-slate-400 hover:text-white"}`}
                                                        aria-pressed={isSelected}
                                                        aria-label={`Mostrar camiseta ${variant.label}`}
                                                    >
                                                        {isSelected ? (
                                                            <motion.span
                                                                layoutId="camisetas-mobile-toggle-pill"
                                                                transition={{ type: "spring", stiffness: 420, damping: 34 }}
                                                                className="absolute inset-0 -z-10 rounded-lg bg-green-500"
                                                            />
                                                        ) : null}
                                                        {variant.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ) : isSpecialEdition ? (
                                        <p className="rounded-xl border border-white/20 bg-[#0f1f15] px-3 py-2 text-center text-xs font-extrabold uppercase tracking-wide text-green-400">
                                            Edición especial 100 años
                                        </p>
                                    ) : null}

                                    {selectedMobileVariant ? (
                                        <article className="w-full rounded-xl border border-white/20 bg-black/20 p-2">
                                            <p className="mb-2 text-center text-xs font-extrabold uppercase tracking-wide text-slate-200">
                                                {selectedMobileVariant.label}
                                            </p>
                                            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg bg-[#08140e]">
                                                {!loadedImages[selectedMobileVariant.camiseta.image] ? (
                                                    <div className="absolute inset-0 bg-[#0b1210]" />
                                                ) : null}
                                                <img
                                                    src={selectedMobileVariant.camiseta.image}
                                                    alt={`${camisetasPair.title} ${selectedMobileVariant.label}`}
                                                    className={`h-full w-full object-contain transition-opacity duration-200 ${loadedImages[selectedMobileVariant.camiseta.image] ? "opacity-100" : "opacity-0"}`}
                                                    loading="eager"
                                                    fetchPriority="high"
                                                    decoding="async"
                                                    onLoad={() => handleImageLoad(selectedMobileVariant.camiseta.image)}
                                                />
                                            </div>
                                            <p className="mt-2 text-center text-sm font-semibold text-slate-200">
                                                Marca: {selectedMobileVariant.camiseta.brand || "Puma"}
                                            </p>
                                        </article>
                                    ) : null}

                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={onPrev}
                                            disabled={!hasPrev || isTransitioning}
                                            className={`${mobileNavButtonClass} text-2xl leading-none`}
                                            aria-label="Camiseta anterior"
                                        >
                                            {hasPrev ? (
                                                <motion.span
                                                    whileTap={{ scale: 0.96 }}
                                                    transition={{ type: "spring", stiffness: 500, damping: 28 }}
                                                    className="absolute inset-0 rounded-xl bg-white/0 hover:bg-white/5"
                                                />
                                            ) : null}
                                            ←
                                        </button>
                                        <button
                                            type="button"
                                            onClick={onNext}
                                            disabled={!hasNext || isTransitioning}
                                            className={`${mobileNavButtonClass} text-2xl leading-none`}
                                            aria-label="Camiseta siguiente"
                                        >
                                            {hasNext ? (
                                                <motion.span
                                                    whileTap={{ scale: 0.96 }}
                                                    transition={{ type: "spring", stiffness: 500, damping: 28 }}
                                                    className="absolute inset-0 rounded-xl bg-white/0 hover:bg-white/5"
                                                />
                                            ) : null}
                                            →
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className={`grid ${variants.length > 1 ? "grid-cols-2 gap-2 sm:gap-3" : "grid-cols-1 gap-3"}`}>
                                    {variants.map((variant) => (
                                        <article
                                            key={variant.id}
                                            className={`min-w-0 rounded-xl border border-white/20 bg-black/20 p-1.5 sm:p-2 ${variants.length === 1 ? "w-full max-w-[250px] mx-auto" : ""}`}
                                        >
                                            <p className="mb-1.5 text-center text-[10px] sm:text-sm font-extrabold uppercase tracking-wide text-slate-200">
                                                {isSpecialEdition ? "Edición especial 100 años" : variant.label}
                                            </p>
                                            <>
                                                <div className="relative aspect-[3/4] w-full overflow-hidden rounded-lg bg-[#08140e]">
                                                    {!loadedImages[variant.camiseta.image] ? (
                                                        <div className="absolute inset-0 bg-[#0b1210]" />
                                                    ) : null}
                                                    <img
                                                        src={variant.camiseta.image}
                                                        alt={`${camisetasPair.title} ${variant.label}`}
                                                        className={`h-full w-full object-contain transition-opacity duration-200 ${loadedImages[variant.camiseta.image] ? "opacity-100" : "opacity-0"}`}
                                                        loading="eager"
                                                        fetchPriority="high"
                                                        decoding="async"
                                                        onLoad={() => handleImageLoad(variant.camiseta.image)}
                                                    />
                                                </div>
                                                <p className="mt-1.5 text-center text-[11px] sm:text-sm font-semibold text-slate-200">
                                                    Marca: {variant.camiseta.brand || "Puma"}
                                                </p>
                                            </>
                                        </article>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );

    return createPortal(modalContent, document.body);
}

export default CamisetasModal;
